import React, { useState, useRef, useCallback} from 'react'
import { Tooltip, Alert } from 'antd'
import { SendOutlined, PictureOutlined, LoadingOutlined, UserOutlined, CloseOutlined } from '@ant-design/icons'
import { ButtonGroup } from './Controls.styles'
import { StyledButton } from '../Button'
import MessageTemplateButton from './MessageTemplateButton'
import { ChatType } from './types'
import { MentionDropdown } from './MentionDropdown'
import { useAdminUsers } from '../../hooks/useAdminUsers'
import { CaseStatusEnum } from '../../constants'
import { Editor } from './Editor'
import { useCaseViewContext } from '../../context'

export const Controls = ({
    chatContext,
    ticketStatus,
    assignedAdmin,
    showModal,
    handleSendMessage,
    handleMarkTicketAsRead,
    handleCloseTicket,
    isAdmin,
    caseStatus,
}) => {
    const { provider, apiClasses } = useCaseViewContext()
    const [messageEntered, setMessageEntered] = useState('');
    const [sendMessageLoading, setSendMessageLoading] = useState(false);
    const [taggedAdminId, setTaggedAdminId] = useState(null);
    const [taggedMentionText, setTaggedMentionText] = useState(null); // Store the mention text (e.g., "@John")
    const [mentionState, setMentionState] = useState({
        visible: false,
        searchTerm: '',
        position: { left: 0, top: 0 },
        selectedIndex: 0
    });
    const editorRef = useRef(null);
    const containerRef = useRef(null);

    const { adminUsers } = useAdminUsers({ apiClasses });

    const isSendMessageDisabled = !messageEntered || sendMessageLoading;

    const getAssignedAdminName = useCallback((rep) => {
        return `${rep.firstName || ''} ${rep.lastName || ''}`.trim() || rep.email
      }, [])

    // Extract plain text from HTML
    const stripHtml = (html) => {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const filterUsers = useCallback((users, searchTerm) => {
        if (!searchTerm) return users;
        const search = searchTerm.toLowerCase();
        return users.filter(user => {
            const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
            const email = (user.email || '').toLowerCase();
            return fullName.includes(search) || email.includes(search);
        });
    }, []);

    // Find mention position in text - looks for the last incomplete @ mention
    const findMentionPosition = useCallback((text) => {
        if (!text) return null;
        
        const plainText = stripHtml(text);
        
        // Simple check: does the text end with @ or @ followed by word characters (no space after)?
        // This handles cases like "@", "@john", "@john doe" (incomplete) vs "@john " (complete)
        const endsWithAtPattern = /@[\w\s]*$/;
        const match = plainText.match(endsWithAtPattern);
        
        if (!match) {
            return null;
        }
        
        const mentionText = match[0]; // e.g., "@" or "@john" or "@john doe"
        const searchTerm = mentionText.substring(1).trim(); // Remove the @
        
        // Check if there's already a complete mention earlier in the text
        // A complete mention is one that has a space after it
        const completeMentionPattern = /@[\w\s]+\s/g;
        const completeMentions = plainText.match(completeMentionPattern) || [];
        
        // If there's a complete mention and this is not the last incomplete one, don't show
        if (completeMentions.length > 0) {
            // Check if the current mention is part of a complete one
            const lastCompleteMention = completeMentions[completeMentions.length - 1];
            const lastCompleteIndex = plainText.lastIndexOf(lastCompleteMention);
            const currentMentionIndex = plainText.lastIndexOf(mentionText);
            
            // If there's a complete mention after our current position, don't show
            if (lastCompleteIndex > currentMentionIndex) {
                return null;
            }
        }
        
        // Return the mention info
        const startIndex = plainText.lastIndexOf(mentionText);
        return {
            start: startIndex,
            end: startIndex + mentionText.length,
            searchTerm: searchTerm,
            fullMatch: mentionText
        };
    }, []);

    const handleMessageChange = useCallback((value) => {
        setMessageEntered(value);
        
        // Check if a tagged mention still exists in the message
        if (taggedAdminId && taggedMentionText) {
            const plainText = stripHtml(value);
            // Escape special regex characters in the mention text
            const escapedMention = taggedMentionText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Check if the exact mention text still exists (case-insensitive)
            // Match the mention followed by a space or end of string to ensure it's complete
            // If any character is deleted or modified, this won't match and we'll clear the tag
            const mentionPattern = new RegExp(escapedMention + '(?=\\s|$)', 'i');
            
            if (!mentionPattern.test(plainText)) {
                // Mention has been deleted or any character modified, clear the tag
                setTaggedAdminId(null);
                setTaggedMentionText(null);
            }
        }

        // Check if admin users are loaded
        if (!adminUsers || adminUsers.length === 0) {
            setMentionState(prev => ({ ...prev, visible: false }));
            return;
        }

        const mentionPos = findMentionPosition(value);
        
        const isMentionAlreadyStyled = mentionPos && value.includes(`<span style="font-weight: bold; background-color: rgba(123, 173, 126, 0.15)`);
        if (mentionPos && mentionPos.searchTerm.length < 50 && !isMentionAlreadyStyled) {
            // Show dropdown - position it above the editor
            setMentionState({
                visible: true,
                searchTerm: mentionPos.searchTerm,
                position: {
                    left: 10,
                    top: -220 // Position above the editor
                },
                selectedIndex: 0
            });
        } else {
            setMentionState(prev => ({ ...prev, visible: false }));
        }
    }, [chatContext, findMentionPosition, adminUsers, taggedAdminId, taggedMentionText]);

    const handleMentionSelect = useCallback((user) => {
        setMentionState(prev => ({ ...prev, visible: false }));
        
        const plainText = stripHtml(messageEntered);
        const mentionPos = findMentionPosition(messageEntered);
        
        if (!mentionPos) return;
        
        const plainBefore = plainText.substring(0, mentionPos.start);
        const plainAfter = plainText.substring(mentionPos.end);
        const firstName = user.firstName || user.email.split('@')[0] || 'User';
        const mentionText = `@${firstName}`;
        const styledMention = `<span style="font-weight: bold; background-color: rgba(123, 173, 126, 0.15); padding: 2px 6px; border-radius: 4px; display: inline-block;">${mentionText}</span>&nbsp; `;
        
        const htmlContent = messageEntered || '';
        const incompleteMention = mentionPos.fullMatch || '';
        const lastIndex = htmlContent.lastIndexOf(incompleteMention);
        const newHtmlContent = lastIndex !== -1
            ? htmlContent.substring(0, lastIndex) + styledMention + htmlContent.substring(lastIndex + incompleteMention.length)
            : plainBefore + styledMention + plainAfter;
        
        setTaggedMentionText(mentionText);
        setTaggedAdminId(user._id);
        setMessageEntered(newHtmlContent);
        

        const editor = editorRef.current;
        
        try {
            editor.setContent(newHtmlContent);
            const body = editor.getBody();
            
            // Find the mention span we just inserted and place cursor after it
            const spans = body.querySelectorAll('span[style*="background-color"]');
            if (spans.length > 0) {
                const lastSpan = spans[spans.length - 1];
                // Find the next text node after the span, or create one
                let nextNode = lastSpan.nextSibling;
                
                if (nextNode && nextNode.nodeType === Node.TEXT_NODE) {
                    // Place cursor at the end of the text node (after the spaces)
                    const range = editor.dom.createRng();
                    range.setStart(nextNode, nextNode.textContent.length);
                    range.setEnd(nextNode, nextNode.textContent.length);
                    editor.selection.setRng(range);
                } else {
                    // No text node after, place cursor right after the span
                    const range = editor.dom.createRng();
                    range.setStartAfter(lastSpan);
                    range.setEndAfter(lastSpan);
                    editor.selection.setRng(range);
                }
            } else {
                // Fallback: place cursor at end
                editor.selection.select(body, true);
                editor.selection.collapse(false);
            }
            editor.focus();
        } catch (error) {
            console.warn('Error updating TinyMCE editor:', error);
        }
        
    }, [messageEntered, findMentionPosition]);

    const handleKeyDown = useCallback((event) => {
        if (mentionState.visible) {
            const filteredUsers = filterUsers(adminUsers, mentionState.searchTerm);

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setMentionState(prev => ({
                    ...prev,
                    selectedIndex: Math.min(prev.selectedIndex + 1, filteredUsers.length - 1)
                }));
                return;
            }
            
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setMentionState(prev => ({
                    ...prev,
                    selectedIndex: Math.max(prev.selectedIndex - 1, 0)
                }));
                return;
            }
            
            if (event.key === 'Enter' && filteredUsers[mentionState.selectedIndex]) {
                event.preventDefault();
                handleMentionSelect(filteredUsers[mentionState.selectedIndex]);
                return;
            }
            
            if (event.key === 'Escape') {
                setMentionState(prev => ({ ...prev, visible: false }));
                return;
            }
        }

        if (!event.shiftKey && event.key === 'Enter') {
            onSubmit();
        }
    }, [mentionState, adminUsers, filterUsers, handleMentionSelect]);

    const onSubmit = async () => {
        if (isSendMessageDisabled) {
            return
        }
        setSendMessageLoading(true);
        const isSuccess = await handleSendMessage(messageEntered, taggedAdminId);
        if (isSuccess) {
            setMessageEntered('');
            setTaggedAdminId(null);
            setTaggedMentionText(null);
        }
        setSendMessageLoading(false);
    }

    const disabledDueToStatus = chatContext === ChatType.PATIENT && [
        CaseStatusEnum.CANCELLED, 
        CaseStatusEnum.ARCHIVE, 
        CaseStatusEnum.DELETE
    ].includes(caseStatus)

    const ButtonGroupByChatType = {
        [ChatType.PATIENT]: (
            <div style={{ width: '100%', display: 'flex', gap: '3px' }}>
                <MessageTemplateButton
                    physicianDetails={provider}
                    setText={setMessageEntered}
                    disabled={disabledDueToStatus}
                />
                <StyledButton
                    id="messaging-patient-attach-file-btn"
                    size="medium"
                    Icon={PictureOutlined}
                    tooltip="Attach Image"
                    onClick={showModal}
                    disabled={disabledDueToStatus}
                />
                <div style={{ marginLeft: 'auto' }}>
                    <StyledButton
                        id="messaging-patient-send-btn"
                        size="medium"
                        Icon={sendMessageLoading ? LoadingOutlined : SendOutlined}
                        tooltip="Send"
                        onClick={onSubmit}
                        disabled={isSendMessageDisabled || disabledDueToStatus}
                    />
                </div>
            </div>
        ),
        [ChatType.ADMIN]: (
            <div style={{ display: 'flex', gap: '3px', justifyContent: 'flex-end', width: '100%' }}>
                {assignedAdmin && (
                    <Alert
                        message={
                            <span>
                                <UserOutlined style={{ marginRight: '6px' }} />
                                Ticket assigned to <strong>{getAssignedAdminName(assignedAdmin)}</strong>
                            </span>
                        }
                        type="info"
                        showIcon={false}
                        style={{ 
                            fontSize: '12px', 
                            background: 'none',
                            border: 'none',
                            marginRight: 'auto',
                        }}
                    />
                )}
                <div style={{ display: 'flex', gap: '3px', marginLeft: 'auto' }}> 
                    { isAdmin && <Tooltip title="Close Ticket">
                        <StyledButton
                            id="messaging-admin-close-ticket-btn"
                            size="medium"
                            Icon={CloseOutlined}
                            color="red"
                            onClick={handleCloseTicket}
                            disabled={ticketStatus === 'closed'}
                        />
                    </Tooltip> }
                    <Tooltip title="Send">
                        <StyledButton
                            id="messaging-admin-send-btn"
                            size="medium"
                            Icon={sendMessageLoading ? LoadingOutlined : SendOutlined}
                            tooltip="Send"
                            onClick={!isSendMessageDisabled ? onSubmit : null}
                        />
                    </Tooltip>
                </div>
            </div>
        ),
    }

    const filteredUsers = mentionState.visible ? filterUsers(adminUsers, mentionState.searchTerm) : [];

    return (
        <div ref={containerRef} style={{ height: '240px', position: 'relative' }}>
            <Editor
                ref={editorRef}
                value={messageEntered}
                onChange={handleMessageChange}
                onKeyDown={handleKeyDown}
                onFocus={handleMarkTicketAsRead}
                disabled={disabledDueToStatus}
                height={200}
            />
            {mentionState.visible && chatContext === ChatType.ADMIN && (
                <MentionDropdown
                    users={filteredUsers}
                    searchTerm={mentionState.searchTerm}
                    selectedIndex={mentionState.selectedIndex}
                    onSelect={handleMentionSelect}
                    position={mentionState.position}
                    visible={mentionState.visible}
                />
            )}
            <ButtonGroup>
                {ButtonGroupByChatType[chatContext] || ButtonGroupByChatType[ChatType.PATIENT]}
            </ButtonGroup>
        </div>
    )
}
